import React from 'react';
import Box from '@material-ui/core/Box';
import Add from '@material-ui/icons/Add';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import { RowActions } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';

import {
  ORDER_BY_OPTIONS,
  SENDOUT_INTERVIEWS_COLUMNS,
  TABLE_KEY
} from './SendoutInterviewsTabContent.constants';
import { useStyles } from './SendoutInterviewsTabContent.styled';
import { formatResponse } from './SendoutInterviewsTabContent.utils';

export const SendoutInterviewsTabContent = ({
  canManageSendout = false,
  onEditInterview = () => {},
  onScheduleInterview = () => {},
  onViewInterviewDetails = () => {},
  sendoutId
}) => {
  const classes = useStyles();

  const initialPreferences = getColumnPreferences(
    TABLE_KEY,
    0,
    ORDER_BY_OPTIONS,
    SENDOUT_INTERVIEWS_COLUMNS
  );

  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    columnsDefinitions: SENDOUT_INTERVIEWS_COLUMNS,
    endpoint: Endpoints.SendoutInterviews.replace(':id', sendoutId),
    apiVersion: 2,
    formatResponse,
    initialPreferences,
    key: TABLE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    shouldScrollOnNavigate: true,
    sendDateWithTimezone: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const handleViewInterviewDetails = ({ rowData }) => {
    if (!rowData) return;

    const item = extractObjectFromDataTable(SENDOUT_INTERVIEWS_COLUMNS, ['id'], rowData);

    onViewInterviewDetails({ interviewId: item.id });
  };

  const handleEditInterview = ({ rowData }) => {
    if (!rowData) return;

    const item = extractObjectFromDataTable(SENDOUT_INTERVIEWS_COLUMNS, ['id'], rowData);

    onEditInterview({ interviewId: item.id, refreshInterviews: getData });
  };

  const actionsColumn = buildRowActionsRendererDefinition({
    actions: [
      { name: RowActions.View, onClick: handleViewInterviewDetails },
      ...(canManageSendout ? [{ name: RowActions.Edit, onClick: handleEditInterview }] : [])
    ],
    columns: SENDOUT_INTERVIEWS_COLUMNS
  });

  const finalColumns = getColumnsToRender([...columns, actionsColumn]);

  return (
    <>
      <div className={classes.scheduleButtonContainer}>
        <When condition={canManageSendout}>
          <FPActionButton
            text={strings.sendoutsV2.drawers.createSendoutInterview.title}
            icon={Add}
            iconPosition="left"
            onClick={() => onScheduleInterview(getData)}
            disabled={isLoading}
          />
        </When>
      </div>
      <Box marginTop={4}>
        <DataTable
          columnOrder={columnOrder?.length ? columnOrder : undefined}
          columns={finalColumns}
          count={count ?? 0}
          data={data}
          draggableColumns={{
            enabled: true
          }}
          elevation={2}
          filter={false}
          isExpandable={false}
          loading={showWholeSkeleton}
          onColumnDisplayClick={handleColumnDisplayChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnSortChange={handleColumnSortChange}
          onPageClick={handlePageChange}
          onPerPageClick={handlePerPageChange}
          onSearchTextChange={handleKeywordChange}
          onToggleFilters={handleFiltersToggle}
          page={page}
          refreshing={isLoading}
          rowsPerPage={perPage}
          searchText={keyword}
          selectableRows="none"
          selectToolbarPlacement="none"
          sortOrder={{ name: orderBy, direction }}
          subtitle={strings.dailyPlan.archiveActions.drawer.table.subtitle}
          title={strings.dailyPlan.archiveActions.drawer.table.title}
        />
      </Box>
    </>
  );
};
